/**
 * Single-selection modules
 *
 * Some permission modules accept exactly ONE subModule (the backend expects a single
 * subModule id next to the module id). For those modules the subModules modal renders
 * radio buttons instead of checkboxes, and enabling the module pre-selects a default
 * subModule instead of every independent one.
 *
 * Currently only Coaching SSO (`coachsso`) behaves this way, with `coachsso_admin` as
 * the default selection.
 */
import { getFeatureDisplayCode } from './hierarchicalFeatures';

// moduleCode -> default subModule code
export const SINGLE_SELECTION_MODULES = {
  coachsso: 'coachsso_admin',
};

const moduleKey = (module) => getFeatureDisplayCode(module?.code);

/**
 * @param {Object} module
 * @returns {boolean} True if the module only accepts one selected subModule
 */
export const isSingleSelectionModule = (module) =>
  Object.prototype.hasOwnProperty.call(SINGLE_SELECTION_MODULES, moduleKey(module));

/**
 * @param {Object} module
 * @returns {string|null} Default subModule code for the module, or null
 */
export const getDefaultSingleSubModuleCode = (module) =>
  SINGLE_SELECTION_MODULES[moduleKey(module)] || null;

/**
 * Finds a subModule by feature code (ignores any `::parent` suffix)
 */
export const findSubModuleByCode = (subModules, code) =>
  (subModules || []).find((subModule) => getFeatureDisplayCode(subModule.code) === code) || null;

/**
 * Returns a copy of the subModules where only `code` is checked
 */
export const selectSingleSubModule = (subModules, code) =>
  (subModules || []).map((subModule) => ({
    ...subModule,
    checked: getFeatureDisplayCode(subModule.code) === code,
  }));

/**
 * Returns a copy of the subModules where only the module's default subModule is checked.
 * Falls back to the first subModule when the default is not in the list.
 */
export const applyDefaultSingleSelection = (module) => {
  const subModules = module?.subModules || [];
  const defaultCode = getDefaultSingleSubModuleCode(module);
  const target = findSubModuleByCode(subModules, defaultCode) || subModules[0];
  return selectSingleSubModule(subModules, target ? getFeatureDisplayCode(target.code) : null);
};

/**
 * Guarantees exactly one checked subModule for an enabled single-selection module:
 * - none checked  -> default subModule
 * - several checked -> keeps the first one
 * Unchanged (all unchecked) when the module itself is not checked.
 */
export const normalizeSingleSelection = (module) => {
  const subModules = module?.subModules || [];
  if (!module?.checked) {
    return subModules.map((subModule) => ({ ...subModule, checked: false }));
  }
  const checked = subModules.filter((subModule) => subModule.checked);
  if (checked.length === 1) return subModules.map((subModule) => ({ ...subModule }));
  if (checked.length === 0) return applyDefaultSingleSelection(module);
  return selectSingleSubModule(subModules, getFeatureDisplayCode(checked[0].code));
};
